import { storage } from './utils/typedStorage';
import { env as developmentLocal } from './env/development-local';
import { env as productionLocal } from './env/production-local';
import { env as developmentDeployed } from './env/development-deployed';
import { env as productionDeployed } from './env/production-deployed';
import type { AppEnvConfig } from './appEnvConfig';

export interface AppEnv extends AppEnvConfig {
  readonly IS_LOCAL: boolean;
  readonly ENV: 'development' | 'production';
}

// Explanation:
// By default react apps process.env always use .env.*.local if it exists if run with NODE_ENV=development,
//   before falling back to the either .env.development or .env.production files.
// The intended use caes is to always run development locally (NODE_ENV=development always when you run yarn start)
//   and production (NODE_ENV=production always when you run yarn build) in deployment

// We want a more flexible way to manage env variables, so we're going to re-implement this ourselves with more optionality and explicit behavior
// Furthermore, we want to be able to have a local .env file that is not gitignored, because:
//   1. development will just be easier if things aren't gitignored
//   2. we genuinely want different variables things in local (like the stytch redirect urls and option to connect to local servers)
//   3. we can just set them in the codebase and not have to worry about the local ones
// because of this, we don't make .env.development.local or .env.development due to the default behaviors causing potential issues and don't use process.env

// TL;DR: Don't use process.env ANYWHERE in the codebase. use env instead

// This breaks standards because env is in /src, but for now that is what we are doing. In front end, nothing sensitive is in .env anyways

// Each branch compares the build's own compile-time constants directly, so the
// bundler drops the configs a build does not select and no build ships another
// environment's server URL or Stytch token (scripts/audit-chunks.mjs checks
// this). An unset REACT_APP_DEPLOY_TARGET means deployed and an unset
// REACT_APP_ENV means production; vite.config.ts rejects any other value.
const selectConfig = (): AppEnvConfig => {
  if (import.meta.env.REACT_APP_ENV === 'development') {
    return import.meta.env.REACT_APP_DEPLOY_TARGET === 'local'
      ? developmentLocal
      : developmentDeployed;
  }
  return import.meta.env.REACT_APP_DEPLOY_TARGET === 'local'
    ? productionLocal
    : productionDeployed;
};

const config = selectConfig();
const reactAppEnv: AppEnv['ENV'] =
  import.meta.env.REACT_APP_ENV === 'development'
    ? 'development'
    : 'production';

const env: AppEnv = {
  ...config,
  IS_LOCAL: import.meta.env.REACT_APP_DEPLOY_TARGET === 'local', // for convenience
  ENV: reactAppEnv,
};

// localStorage key holding a manual backend URL override. Lets a single
// (non-production) frontend point at any backend — e.g. a per-PR Render preview
// backend — without rebuilding. Set/cleared via the in-app BackendSelector.
export const BACKEND_OVERRIDE_STORAGE_KEY = 'salvBackendUrlOverride';

// Only allow overriding the backend in non-production builds, so a production
// frontend can never be pointed at an arbitrary backend.
export const isBackendOverrideAllowed = (): boolean =>
  reactAppEnv !== 'production';

// The backend URL configured by the build (before any manual override).
export const DEFAULT_SERVER_URL = config.REACT_APP_SERVER_URL;

const toWebSocketUrl = (httpUrl: string): string =>
  httpUrl.replace(/^http(s?):\/\//, 'ws$1://');

export const getBackendOverride = (): string | null => {
  if (!isBackendOverrideAllowed()) return null;
  return storage.getDynamic(BACKEND_OVERRIDE_STORAGE_KEY) || null;
};

// Apply a stored override (if any) to the live env object. Mutating env here —
// before any consumer imports it — keeps a single source of truth for the
// backend URL across axios, websockets, and ad-hoc fetches.
function applyBackendOverride(): void {
  const override = getBackendOverride();
  if (!override) return;
  const normalized = override.trim().replace(/\/+$/, '');
  if (!/^https?:\/\//.test(normalized)) return;
  env.REACT_APP_SERVER_URL = normalized;
  env.REACT_APP_WS_URL = toWebSocketUrl(normalized);
}

// Render-specific configuration for pull request frontend previews. We no longer
// auto-detect the backend by pinging a derived hostname; the backend is chosen
// via the build's env or the manual override above. We still align Stytch's
// redirect URL with the preview's own hostname so auth works.
function configurePullRequestPreview(): void {
  if (
    import.meta.env.REACT_APP_IS_PULL_REQUEST === 'true' &&
    import.meta.env.REACT_APP_ENV === 'development'
  ) {
    const frontendHostname = import.meta.env.REACT_APP_RENDER_EXTERNAL_HOSTNAME;
    if (frontendHostname) {
      env.REACT_APP_STYTCH_REDIRECT_URL = `https://${frontendHostname}/authenticate`; // account for this in stytch
    }
  }
}

configurePullRequestPreview();
applyBackendOverride();

export default env;
